/**
 * elkjs-based hierarchical layout for the mesh.
 *
 * Two layout modes:
 *   - "analysis" — flat hierarchical layout (one repo, all agents)
 *   - "tenant"   — repo swimlanes (one column per repo, ordered by
 *                  scan_count desc → agent_count desc)
 *
 * Synthetic nodes (mcp_server::, a2a_endpoint::) are pinned to the
 * right of the layout to read as "external" — the eye naturally
 * follows in-scope agents on the left to integrations on the right.
 *
 * We use elkjs because it produces consistent hierarchical layouts at
 * the 100-agent scale without the click-and-drag dance that
 * dagre/cytoscape force layouts require. Layouts are deterministic
 * given the same input — important for the drift-history view in V2.
 */
import ELK, { ElkNode } from 'elkjs/lib/elk.bundled.js'
import type { Edge, Node } from '@xyflow/react'
import type { MeshEdge, MeshNode, MeshResponse } from './types'

const elk = new ELK()

const NODE_W = 220
const NODE_H = 88

/**
 * Convert a MeshResponse into the (nodes, edges) that react-flow expects,
 * with positions computed by elkjs.
 *
 * elk options are tuned for the readability of a small-medium graph
 * (20-200 nodes); at >300 nodes the click-into-neighborhood view (V2)
 * is the right call rather than re-tuning here.
 */
export async function layoutMesh(mesh: MeshResponse): Promise<{
  nodes: Node[]
  edges: Edge[]
}> {
  const isTenant = mesh.scope === 'tenant'

  // Group nodes by repo for swimlane layout (tenant scope)
  const nodesByRepo = new Map<string, MeshNode[]>()
  const synthetic: MeshNode[] = []
  for (const n of mesh.nodes) {
    if (n._source === 'synthetic') {
      synthetic.push(n)
      continue
    }
    const key = n.repo_id || '__no_repo__'
    if (!nodesByRepo.has(key)) nodesByRepo.set(key, [])
    nodesByRepo.get(key)!.push(n)
  }

  // Build ELK input
  const elkChildren: ElkNode[] = []

  if (isTenant) {
    // Repo swimlanes — each repo is a top-level group with its agents inside
    const repoOrder = mesh.repos
      .slice()
      .sort((a, b) => (b.scan_count ?? 0) - (a.scan_count ?? 0) || b.agent_count - a.agent_count)

    for (const repo of repoOrder) {
      const agents = nodesByRepo.get(repo.id) || []
      if (agents.length === 0) continue
      elkChildren.push({
        id: `repo::${repo.id}`,
        layoutOptions: {
          'elk.algorithm': 'layered',
          'elk.direction': 'DOWN',
          'elk.padding': '[top=40,left=20,right=20,bottom=20]',
        },
        children: agents.map((a) => ({ id: a.id, width: NODE_W, height: NODE_H })),
      })
    }
  } else {
    // Analysis scope — flat, all agents in one layer
    const agents = mesh.nodes.filter((n) => n._source !== 'synthetic')
    for (const a of agents) {
      elkChildren.push({ id: a.id, width: NODE_W, height: NODE_H })
    }
  }

  // Synthetic nodes — always at the right
  for (const s of synthetic) {
    elkChildren.push({ id: s.id, width: NODE_W, height: NODE_H })
  }

  const elkGraph: ElkNode = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.layered.spacing.nodeNodeBetweenLayers': '80',
      'elk.spacing.nodeNode': '40',
      'elk.padding': '[top=20,left=20,right=20,bottom=20]',
    },
    children: elkChildren,
    edges: mesh.edges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  }

  const laid = await elk.layout(elkGraph)

  // Convert back to react-flow shape — flattening repo groups so nodes
  // have absolute positions (react-flow groups add visual nesting but we
  // get more control rendering swimlane labels separately).
  const nodes: Node[] = []
  for (const child of laid.children || []) {
    if (child.id.startsWith('repo::')) {
      const repoId = child.id.slice('repo::'.length)
      const repo = mesh.repos.find((r) => r.id === repoId)
      // Repo header node (visual swimlane label)
      nodes.push({
        id: child.id,
        type: 'repoHeader',
        position: { x: child.x ?? 0, y: (child.y ?? 0) - 30 },
        data: { repo, width: child.width ?? 200 },
        draggable: false,
        selectable: false,
      })
      for (const inner of child.children || []) {
        const meshNode = mesh.nodes.find((n) => n.id === inner.id)
        if (!meshNode) continue
        nodes.push({
          id: meshNode.id,
          type: 'mesh',
          position: {
            x: (child.x ?? 0) + (inner.x ?? 0),
            y: (child.y ?? 0) + (inner.y ?? 0),
          },
          data: { node: meshNode },
        })
      }
    } else {
      const meshNode = mesh.nodes.find((n) => n.id === child.id)
      if (!meshNode) continue
      nodes.push({
        id: meshNode.id,
        type: 'mesh',
        position: { x: child.x ?? 0, y: child.y ?? 0 },
        data: { node: meshNode },
      })
    }
  }

  const edges: Edge[] = mesh.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: 'mesh',
    data: { edge: e },
    animated: e.type === 'a2a_call',
  }))

  return { nodes, edges }
}
